import type { DateTime } from 'luxon';
import { get, type Writable } from 'svelte/store';
import { now } from './time';

interface Identifiable {
	id: string | number;
}

interface Archivable extends Identifiable {
	archivedAt: DateTime | string | null;
}

function matchesId(a: string | number, b: string | number): boolean {
	return typeof a === 'string' && typeof b === 'string' ? a.trim() === b.trim() : a === b;
}

function isSameItem<T extends Identifiable>(item: T, candidate: unknown): candidate is T {
	return (
		typeof candidate === 'object' &&
		candidate !== null &&
		'id' in candidate &&
		matchesId((candidate as Identifiable).id, item.id)
	);
}

/**
 * Optimistic create/update for an item in a Writable<T[]> with automatic rollback on error.
 */
export async function optimisticSave<T extends Identifiable, R = unknown>(
	store: Writable<T[]>,
	item: T,
	mutation: () => Promise<R>
): Promise<T> {
	const previous = get(store);
	const backup = previous.find((i) => matchesId(i.id, item.id));
	const isNew = !backup;

	// 1. Immediate optimistic UI update
	store.update((list) =>
		isNew ? [...list, item] : list.map((i) => (matchesId(i.id, item.id) ? item : i))
	);

	try {
		// 2. Perform backend API mutation
		const result = await mutation();
		if (isSameItem(item, result)) {
			store.update((list) => list.map((i) => (matchesId(i.id, item.id) ? result : i)));
			return result;
		}
		return item;
	} catch (error: unknown) {
		// 3. Rollback to initial snapshot state
		store.set(previous);
		throw error;
	}
}

/**
 * Optimistic hard delete of an item from a Writable<T[]> with automatic rollback on error.
 */
export async function optimisticDelete<T extends Identifiable, R = void>(
	store: Writable<T[]>,
	itemOrId: T | T['id'],
	mutation: () => Promise<R>
): Promise<R> {
	const id = typeof itemOrId === 'object' ? itemOrId.id : itemOrId;
	const previous = get(store);

	// 1. Immediate optimistic UI removal
	store.update((list) => list.filter((i) => !matchesId(i.id, id)));

	try {
		// 2. Perform backend API mutation
		return await mutation();
	} catch (error: unknown) {
		// 3. Rollback to initial snapshot state
		store.set(previous);
		throw error;
	}
}

/**
 * Optimistic archive (soft delete) of an item in a Writable<T[]> with automatic rollback on error.
 */
export async function optimisticArchive<T extends Archivable, R = void>(
	store: Writable<T[]>,
	item: T,
	mutation: () => Promise<R>,
	archiveTimestamp: DateTime = get(now)
): Promise<R> {
	const previous = get(store);

	// 1. Immediate optimistic UI archive
	store.update((list) =>
		list.map((i) => (matchesId(i.id, item.id) ? { ...i, archivedAt: archiveTimestamp } : i))
	);

	try {
		// 2. Perform backend API mutation
		return await mutation();
	} catch (error: unknown) {
		// 3. Rollback to initial snapshot state
		store.set(previous);
		throw error;
	}
}

/**
 * Optimistic reordering of items in a Writable<T[]> with automatic rollback on error.
 */
export async function optimisticReorder<T extends Identifiable, R = void>(
	store: Writable<T[]>,
	reorderedItems: T[],
	mutation: () => Promise<R>
): Promise<R> {
	const previous = get(store);

	// 1. Immediate optimistic UI reorder
	store.set(reorderedItems);

	try {
		// 2. Perform backend API mutation
		return await mutation();
	} catch (error: unknown) {
		// 3. Rollback to initial snapshot state
		store.set(previous);
		throw error;
	}
}

/**
 * Optimistic set for a single-object Writable<T> with automatic rollback on error.
 */
export async function optimisticSet<T, R = unknown>(
	store: Writable<T>,
	item: T,
	mutation: () => Promise<R>
): Promise<T> {
	const previous = get(store);

	// 1. Immediate optimistic UI update
	store.set(item);

	try {
		// 2. Perform backend API mutation
		await mutation();
		return item;
	} catch (error: unknown) {
		// 3. Rollback to initial snapshot state
		store.set(previous);
		throw error;
	}
}
